import { Link } from 'react-router-dom';
import {
  applyModeLabel,
  applyModeStyle,
  resolveSupportTierKey,
  supportTierCompact,
  supportTierStyle,
} from '../../utils/applyDisclosure';
import GlowCard from './GlowCard';
import CompanyLogo from './CompanyLogo';
import MatchRing from './MatchRing';
import DeadlineBar from './DeadlineBar';
import {
  CAT, isClosingSoon, daysLeft, whyItFits, ClockIcon, LocationIcon, ArrowRightIcon, SparkIcon,
} from './jobCardUtils';

const chip = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
  background: bg, color, padding: '4px 10px', borderRadius: 8,
  ...(border ? { border: `1px solid ${border}` } : {}),
});

export default function JobCard({ job, matchScore, skillFreq }) {
  const cat = CAT[job.category] || CAT.Other;
  const mode = job.apply_mode || 'manual';
  const modeStyle = applyModeStyle(mode);
  const tierKey = resolveSupportTierKey(job);
  const tierCompactVal = supportTierCompact(tierKey);
  const tierStyle = tierCompactVal ? supportTierStyle(tierCompactVal) : null;
  const fits = whyItFits(job, 3, skillFreq);
  // Capitalize the first letter of each item (raw ai_notes phrases often start
  // lowercase) and join into one readable line.
  const fitsText = fits.map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)).join('  ·  ');

  return (
    <GlowCard style={{ width: '100%', minHeight: 212 }}>
      {(hovered) => (
        <>
          {/* category top accent + corner wash */}
          <div style={{ height: 3, borderRadius: '16px 16px 0 0', background: `linear-gradient(90deg, ${cat.accent}CC, ${cat.accent}00)` }} />
          <div
            aria-hidden
            style={{
              position: 'absolute', top: 0, right: 0, width: 150, height: 150, pointerEvents: 'none',
              background: `radial-gradient(circle at 100% 0%, ${cat.accent}14 0%, transparent 60%)`,
            }}
          />

          <Link
            to={`/jobs/${job.id}`}
            style={{ display: 'flex', flexDirection: 'column', flex: 1, textDecoration: 'none', padding: '18px 20px 22px' }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <CompanyLogo
                name={job.company_name}
                website={job.company_website}
                logoUrl={job.company_logo}
                accent={cat.accent}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 11, color: 'var(--tandm-muted)', margin: '0 0 3px', fontWeight: 600 }}>
                  {job.company_name}
                </p>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--tandm-ink)', margin: 0, lineHeight: 1.3, letterSpacing: '-0.015em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {job.title}
                </h3>
              </div>
              {matchScore != null && <MatchRing score={matchScore} />}
            </div>

            {/* Meta region: badges, with hover-reveal "why it fits" overlay */}
            <div style={{ position: 'relative', flex: 1, marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={chip(cat.light, cat.accent)}>
                  <cat.icon color={cat.accent} /> {job.category}
                </span>
                <span style={chip(modeStyle.bg, modeStyle.color)}>{applyModeLabel(mode)}</span>
                {tierCompactVal && <span style={{ ...chip(tierStyle.bg, tierStyle.color, tierStyle.border), fontWeight: 700 }}>{tierCompactVal}</span>}
                {isClosingSoon(job.deadline) && (
                  <span style={chip('var(--tandm-danger-bg)', 'var(--tandm-danger-fg)')}>
                    <ClockIcon /> Closing soon
                  </span>
                )}
                {job.pending_questions?.length > 0 && (
                  <span style={chip('var(--tandm-warning-bg)', 'var(--tandm-warning-fg)')}>
                    {job.pending_questions.length} {job.pending_questions.length === 1 ? 'question' : 'questions'} to answer
                  </span>
                )}
              </div>

              {fits.length > 0 && (
                <div
                  aria-hidden={!hovered}
                  style={{
                    position: 'absolute', inset: '-4px -2px', borderRadius: 10,
                    background: 'var(--tandm-surface)',
                    opacity: hovered ? 1 : 0,
                    transform: hovered ? 'translateY(0)' : 'translateY(4px)',
                    transition: 'opacity 0.2s ease, transform 0.2s var(--tandm-ease)',
                    pointerEvents: 'none',
                    display: 'flex', flexDirection: 'column', gap: 6,
                    overflow: 'hidden',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, color: 'var(--tandm-accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <SparkIcon /> Why it fits you
                  </span>
                  <p
                    title={fitsText}
                    style={{
                      margin: 0, paddingRight: 6, fontSize: 11.5, fontWeight: 600, lineHeight: 1.5,
                      color: 'var(--tandm-accent-deep)',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}
                  >
                    {fitsText}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid var(--tandm-line-soft)', marginTop: 'auto' }}>
              {job.location && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--tandm-muted)' }}>
                  <LocationIcon /> {job.location}
                </span>
              )}
              {job.duration && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--tandm-muted)' }}>
                  <ClockIcon /> {job.duration}
                </span>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                {job.deadline && (
                  <span style={{ fontSize: 11, fontWeight: isClosingSoon(job.deadline) ? 600 : 400, color: isClosingSoon(job.deadline) ? 'var(--tandm-danger-fg)' : 'var(--tandm-faint)' }}>
                    {daysLeft(job.deadline)}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--tandm-accent)', display: 'flex', alignItems: 'center', gap: 3, fontWeight: 600 }}>
                  View <ArrowRightIcon />
                </span>
              </div>
            </div>
          </Link>

          <DeadlineBar deadline={job.deadline} />
        </>
      )}
    </GlowCard>
  );
}
